import { useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';

import { useGetUsersQuery, useSearchUsersQuery, UserListFilters } from 'api/adminApi';
import { ONBOARDING_STEPS, ONBOARDING_STEP_TITLES, OnboardingStepId } from 'utils/onboardingSteps';

import styles from './style.module.scss';

const PAGE_SIZE = 50;

type StatusFilter = 'all' | 'in_progress' | 'completed';
type StepFilter = 'all' | OnboardingStepId;
type PassportStatusFilter = 'all' | 'not_uploaded' | 'pending' | 'approved' | 'rejected';

const PASSPORT_STATUS_LABELS: Record<string, string> = {
    pending: 'Новый клиент',
    approved: 'Проверено',
    rejected: 'Отклонено',
};

const getPassportStatusLabel = (status: 'pending' | 'approved' | 'rejected' | null): string =>
    (status && PASSPORT_STATUS_LABELS[status]) || '—';

export const Users = () => {
    const [searchName, setSearchName] = useState('');
    const [searchEmail, setSearchEmail] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [stepFilter, setStepFilter] = useState<StepFilter>('all');
    const [passportStatusFilter, setPassportStatusFilter] = useState<PassportStatusFilter>('all');
    const [page, setPage] = useState(0);

    const isSearching = searchName.trim() !== '' || searchEmail.trim() !== '';

    // Фильтры уходят в запрос на сервер, а не режут уже загруженную страницу —
    // иначе на конкретной странице просто может не оказаться ни одного подходящего
    // пользователя, и фильтр выглядит "сломанным", хотя логика была верной.
    const filters: UserListFilters = {
        onboardingStep: stepFilter === 'all' ? undefined : stepFilter,
        passportStatus: passportStatusFilter === 'all' ? undefined : passportStatusFilter === 'not_uploaded' ? 'none' : passportStatusFilter,
        onboardingRequired: statusFilter === 'all' ? undefined : statusFilter === 'in_progress' ? 'true' : 'false',
    };

    const { data: listData, isFetching: isListFetching } = useGetUsersQuery(
        { offset: page * PAGE_SIZE, limit: PAGE_SIZE, ...filters },
        { skip: isSearching }
    );

    const { data: searchData, isFetching: isSearchFetching } = useSearchUsersQuery(
        { fullName: searchName.trim() || undefined, email: searchEmail.trim() || undefined, ...filters },
        { skip: !isSearching }
    );

    const isLoading = isSearching ? isSearchFetching : isListFetching;
    const users = isSearching ? (searchData ?? []) : (listData?.users ?? []);

    const totalCount = listData?.count ?? 0;
    const hasNextPage = !isSearching && (page + 1) * PAGE_SIZE < totalCount;
    const hasPrevPage = !isSearching && page > 0;

    const resetToFirstPage = () => setPage(0);

    return (
        <div className={styles.page}>
            <h1>Пользователи</h1>

            <div className={styles.filters}>
                <input
                    placeholder='Поиск по имени'
                    value={searchName}
                    onChange={(event) => {
                        setSearchName(event.target.value);
                        resetToFirstPage();
                    }}
                    className={styles.input}
                />

                <input
                    placeholder='Поиск по email'
                    value={searchEmail}
                    onChange={(event) => {
                        setSearchEmail(event.target.value);
                        resetToFirstPage();
                    }}
                    className={styles.input}
                />

                <select
                    value={statusFilter}
                    onChange={(event) => {
                        setStatusFilter(event.target.value as StatusFilter);
                        resetToFirstPage();
                    }}
                    className={styles.select}
                >
                    <option value='all'>Все</option>
                    <option value='in_progress'>В процессе онбординга</option>
                    <option value='completed'>Завершили</option>
                </select>

                <select
                    value={stepFilter}
                    onChange={(event) => {
                        setStepFilter(
                            event.target.value === 'all' ? 'all' : (Number(event.target.value) as OnboardingStepId)
                        );
                        resetToFirstPage();
                    }}
                    className={styles.select}
                >
                    <option value='all'>Любой этап</option>
                    {ONBOARDING_STEPS.map((step) => (
                        <option
                            key={step.id}
                            value={step.id}
                        >
                            {step.id}. {step.title}
                        </option>
                    ))}
                </select>

                <select
                    value={passportStatusFilter}
                    onChange={(event) => {
                        setPassportStatusFilter(event.target.value as PassportStatusFilter);
                        resetToFirstPage();
                    }}
                    className={styles.select}
                >
                    <option value='all'>Любой статус</option>
                    <option value='not_uploaded'>Без статуса</option>
                    <option value='pending'>Новый клиент</option>
                    <option value='approved'>Проверено</option>
                    <option value='rejected'>Отклонено</option>
                </select>
            </div>

            {isLoading ? (
                <div>Загрузка…</div>
            ) : (
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>ФИО</th>
                            <th>Email</th>
                            <th>Дата регистрации</th>
                            <th>Этап онбординга</th>
                            <th>Паспорт</th>
                            <th>Статус</th>
                            <th />
                        </tr>
                    </thead>

                    <tbody>
                        {users.map((user) => (
                            <tr key={user._id}>
                                <td>{user.fullName}</td>
                                <td>{user.email}</td>
                                <td>{dayjs(user.dateCreated).format('DD.MM.YYYY')}</td>
                                <td>
                                    {user.onboardingStep}. {ONBOARDING_STEP_TITLES[user.onboardingStep]}
                                </td>
                                <td>{user.passportUploadedAt ? 'Загружен' : 'Не загружен'}</td>
                                <td>{getPassportStatusLabel(user.passportStatus)}</td>
                                <td>
                                    <Link to={`/users/${user._id}`}>Открыть</Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!isSearching && (
                <div className={styles.pagination}>
                    <button
                        disabled={!hasPrevPage}
                        onClick={() => setPage((p) => p - 1)}
                    >
                        ← Назад
                    </button>

                    <span>Стр. {page + 1}</span>

                    <button
                        disabled={!hasNextPage}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Вперёд →
                    </button>
                </div>
            )}
        </div>
    );
};
